import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { postApi } from '../Services/apiService';

/** JWT from screening link query; kept separate from login `token` in localStorage. */
export const SCREENING_CALL_TOKEN_KEY = 'screening_call_token';

/** Detect if the user agent is mobile (phone/tablet). */
function useIsMobile() {
  return useMemo(() => {
    if (typeof navigator === 'undefined' || !navigator.userAgent) return false;
    const ua = navigator.userAgent.toLowerCase();
    const mobileKeywords = [
      'android',
      'webos',
      'iphone',
      'ipad',
      'ipod',
      'blackberry',
      'iemobile',
      'opera mini',
      'opera mobi',
      'mobile',
    ];
    return mobileKeywords.some((k) => ua.includes(k));
  }, []);
}

function getLoggedInPath() {
  const userDataString = localStorage.getItem('userData');
  if (!userDataString) return null;

  try {
    const userData = JSON.parse(userDataString);
    let role = userData?.user_details?.role || userData?.role || localStorage.getItem('role');
    if (role !== undefined && role !== null) {
      role = typeof role === 'string' ? parseInt(role, 10) : role;
    }
    if (role === 2) return '/tenant/properties';
    if (role === 1) return '/landlord/dashboard';
  } catch {
    // fall through
  }
  return null;
}

function StartCallCloseButton({ onClick }) {
  return (
    <button type="button" className="start-call-close-btn" onClick={onClick} aria-label="Close">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path
          d="M18 6L6 18M6 6L18 18"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function StartCallPage({ children }) {
  return <div className="start-call-page">{children}</div>;
}

export default function StartCall() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const isMobile = useIsMobile();

  const handleClose = useCallback(() => {
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      navigate('/login', { replace: true });
      return;
    }
    const loggedInPath = getLoggedInPath();
    navigate(loggedInPath || '/login', { replace: true });
  }, [navigate]);

  const [startCallStatus, setStartCallStatus] = useState('idle'); // 'idle' | 'loading' | 'done' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // Persist screening JWT under its own key so login `token` is never cleared or overwritten.
  useEffect(() => {
    if (!token) {
      setStartCallStatus('error');
      setErrorMessage('Invalid or missing link.');
      return;
    }

    try {
      localStorage.setItem(SCREENING_CALL_TOKEN_KEY, token);
    } catch (e) {
      // Quota or private mode — continue; API still receives token from URL
    }

    let cancelled = false;
    setStartCallStatus('loading');

    const startCall = async () => {
      try {
        await postApi('/screening/start-call', { token });
        if (!cancelled) setStartCallStatus('done');
      } catch (err) {
        if (!cancelled) {
          setStartCallStatus('error');
          setErrorMessage(
            err?.response?.data?.message || err?.message || 'Failed to start the call. Please try again.'
          );
        }
      }
    };

    startCall();
    return () => { cancelled = true; };
  }, [token]);

  // No token
  if (!token) {
    return (
      <StartCallPage>
        <div className="start-call-card start-call-message-card">
          <StartCallCloseButton onClick={handleClose} />
          <p className="start-call-message-text">Invalid or missing link.</p>
          <p className="start-call-message-sub">Please use the link sent to you to start the screening call.</p>
        </div>
      </StartCallPage>
    );
  }

  // Desktop / laptop: wait for start-call API; on success tell user to use phone; on error show API message (e.g. 400)
  if (!isMobile) {
    const desktopPending = startCallStatus === 'idle' || startCallStatus === 'loading';
    return (
      <StartCallPage>
        <div className="start-call-card start-call-message-card">
          {startCallStatus !== 'done' && <StartCallCloseButton onClick={handleClose} />}
          {desktopPending && (
            <div className="start-call-spinner-wrap">
              <div className="start-call-spinner" />
            </div>
          )}
          {startCallStatus === 'error' && (
            <p className="start-call-error-text start-call-desktop-error">{errorMessage}</p>
          )}
          {startCallStatus === 'done' && (
            <div className="start-call-desktop-content">
              <div className="start-call-desktop-icon-wrap">
                <svg className="start-call-desktop-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="start-call-desktop-text">
                <div className="start-call-message-title-row">
                  <h1 className="start-call-message-title">Open your phone</h1>
                  <StartCallCloseButton onClick={handleClose} />
                </div>
                <p className="start-call-message-text">
                  A call is about to start. Please have your phone ready to receive the screening call.
                </p>
                <p className="start-call-message-sub">Answer when your phone rings to begin the automated screening.</p>
                <p className="start-call-message-sub">It is only valid for 30 minutes.</p>
              </div>
            </div>
          )}
        </div>
      </StartCallPage>
    );
  }

  // Mobile: show Calling UI (screenshot 1)
  return (
    <StartCallPage>
      <div className="start-call-top">
        <div className="start-call-icon-wrap">
          <svg className="start-call-phone-icon" viewBox="0 0 84 85" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="84" height="85" rx="18" fill="#43BF67" fillOpacity="0.34"/>
            <path d="M52 44.5v4.2a4.2 4.2 0 01-4.6 4.2 20.8 20.8 0 01-15.2-15.2 4.2 4.2 0 014.2-4.6h4.2a4.2 4.2 0 014.2 3.6c.2 1.2.5 2.3.9 3.4a4.2 4.2 0 01-2.3 5.6l-1.7.8a16.8 16.8 0 007.9 7.9l.8-1.7a4.2 4.2 0 015.6-2.3c1 .4 2.2.7 3.4.9a4.2 4.2 0 013.6 4.2z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h1 className="start-call-title">Calling</h1>
        <p className="start-call-subtitle">Automated Calling</p>
      </div>

      <div className="start-call-card">
        <StartCallCloseButton onClick={handleClose} />
        {startCallStatus === 'loading' && (
          <div className="start-call-spinner-wrap">
            <div className="start-call-spinner" />
          </div>
        )}
        {startCallStatus === 'done' && (
          <p className="start-call-status-text">Call started. Please answer when your phone rings.</p>
        )}
        {startCallStatus === 'error' && (
          <p className="start-call-error-text">{errorMessage}</p>
        )}
      </div>
    </StartCallPage>
  );
}
